import { ExternalLink } from '@orgus/ui/icons';
import type { DeployTarget, Ts } from '@orgus/protocol';
import { ListRow, Panel, RelativeTime, RowCell, RowList, RowStatus, RowTitle, Tooltip, plural } from '@orgus/ui';
import type { ReactNode } from 'react';
import { heldDeploy } from '../project-health/model.ts';
import { reportedTargetStatus } from './model.ts';

export interface ReportedTarget {
  t: DeployTarget;
  /** The machine whose agent read it. */
  machine: string;
  /** When the project last wrote deploys.json, when known. */
  updatedAt: Ts | null;
}

/** Only an http(s) address opens: the hub keeps what a node reported as text. */
const openable = (url: string | null): url is string => !!url && /^https?:\/\/\S+$/i.test(url);

function portOf(url: string): string | null {
  try {
    const u = new URL(url);
    return u.port || (u.protocol === 'https:' ? '443' : '80');
  } catch {
    return null;
  }
}

/** What every row shares, said once in the header instead of on each row: the machine, and when the project last reported. */
function sharedFacts(items: readonly ReportedTarget[]): { machine: string | null; updatedAt: Ts | null } {
  const machines = new Set(items.map((x) => x.machine));
  const times = new Set(items.map((x) => x.updatedAt));
  return { machine: machines.size === 1 ? items[0]!.machine : null, updatedAt: times.size === 1 ? (items[0]!.updatedAt ?? null) : null };
}

/** A status short enough for its column: "Last deploy failed" reads "Deploy failed" (the full words are its tooltip). */
export function shortStatus(label: string): string {
  return label.replace(/^last\s+/i, '').replace(/^./, (c) => c.toUpperCase());
}

/**
 * One target the project reports: what is live, its own state in words, where it serves and
 * since when. Read only. Every row has the same columns (an address opens from the row itself,
 * its icon in a fixed column), so versions and states line up down the list. On a phone the row
 * keeps its name and status and drops the version, port and since.
 */
function ReportedEnvRow({ t, machine, updatedAt, shared }: ReportedTarget & { shared: ReturnType<typeof sharedFacts> }) {
  const st = reportedTargetStatus(t);
  const port = openable(t.url) ? portOf(t.url) : null;
  const failed = t.last?.result === 'failed';
  const held = heldDeploy(t);
  // A second line only when there is something to act on: the last deploy failed or did not happen (with why), or main is ahead of what is live.
  const note = failed
    ? `The last deploy${t.last?.version ? ` of ${t.last.version}` : ''} failed${t.live ? `; ${t.live.version} is still live` : ''}`
    : held
      ? (t.last?.message ?? `The last deploy${t.last?.version ? ` of ${t.last.version}` : ''} was ${held}${t.live ? `; ${t.live.version} is still live` : ''}`)
      : t.behind && t.mainVersion
        ? `main is at ${t.mainVersion}`
        : null;
  // Facts a row keeps for itself only when they differ between rows.
  const own = [shared.machine ? null : machine, shared.updatedAt == null && updatedAt != null ? <>Reported <RelativeTime ts={updatedAt} /></> : null].filter((x) => x != null);
  return (
    <ListRow
      aria-label={`${t.label}, ${st.label}`}
      multiline={!!note}
      onOpen={openable(t.url) ? () => window.open(t.url!, '_blank', 'noopener,noreferrer') : undefined}
      trail={
        <RowCell width="w-28" numeric hideBelow="sm">
          {t.live?.at != null && (
            <span>
              Since <RelativeTime ts={t.live.at} />
            </span>
          )}
        </RowCell>
      }
    >
      <RowTitle title={t.label} meta={note ?? (own.length ? <span className="flex gap-2">{own.map((x, i) => <span key={i}>{x}</span>)}</span> : undefined)} />
      <RowCell width="w-24" hideBelow="sm" className="font-mono text-ui text-fg">
        {t.live?.version}
      </RowCell>
      <RowCell width="w-32">
        <Tooltip content={note ?? st.label} enabled={shortStatus(st.label) !== st.label || !!note}>
          <span className="flex min-w-0">
            <RowStatus {...st} label={shortStatus(st.label)} />
          </span>
        </Tooltip>
      </RowCell>
      <RowCell width="w-20" hideBelow="md">
        {port && `Port ${port}`}
      </RowCell>
      <RowCell width="w-4" hideBelow="sm">
        {openable(t.url) && <ExternalLink className="size-4 text-fg-subtle" aria-label={`Opens ${t.label}`} />}
      </RowCell>
    </ListRow>
  );
}

/**
 * The environments a project reports itself (Project Protocol deploys.json, ADR 0052), such as
 * Local main from orgus follow-main (ADR 0055): one row each, in a panel whose header names the
 * project (or says "Reported by" it) and carries what every row shares. Orgus shows them; it
 * never deploys to them.
 */
export function ReportedEnvironments({ title, label, items }: { title: ReactNode; /** The panel's accessible name: "Environments Orgus reports". */ label: string; items: readonly ReportedTarget[] }) {
  if (!items.length) return null;
  const shared = sharedFacts(items);
  const facts = (shared.machine || shared.updatedAt != null) && (
    <span className="flex min-w-0 items-center gap-3 pr-2 text-caption text-fg-subtle">
      {shared.machine && <span className="hidden truncate sm:inline">{shared.machine}</span>}
      {shared.updatedAt != null && (
        <span className="shrink-0">
          Reported <RelativeTime ts={shared.updatedAt} />
        </span>
      )}
    </span>
  );
  return (
    <Panel title={title} sub={<span className="max-sm:hidden">{plural(items.length, 'environment')}</span>} actions={facts || undefined} flush aria-label={label}>
      <RowList label={label}>
        {items.map((x) => (
          <ReportedEnvRow key={`${x.machine}:${x.t.id}`} {...x} shared={shared} />
        ))}
      </RowList>
    </Panel>
  );
}
